<template>
  <div class="field-builder">
    <div class="row bottom g-1">
      <component :is="enablePresetSelect ? 'DropdownComponent' : 'div'" v-if="isNuxt && sortingConfig">
        <ButtonComponent
          :label="triggerLabel"
          icon-right="fa-pro-regular:angle-down"
          @click="enablePresetSelect ? null : (open = !open)"
        />
        <template #dropdown>
          <div>
            <ButtonComponent
              :label="buildMode ? 'Legg til felter manuelt' : $t('sortingTitle')"
              icon="fa-pro-regular:arrow-right"
              @click="open = !open"
            />

            <span class="field-builder__category">{{ `Velg et oppsett fra andre ${presetTitle.toLowerCase()}:` }}</span>
            <ButtonComponent
              v-for="value in presets"
              :label="value.title"
              icon="fa-pro-light:newspaper"
              @click="preset = value.id"
            />
          </div>
        </template>
      </component>
    </div>

    <ModalComponent :show="open">
      <Webcomponent
        class="field-builder__content"
        :value="valueComp"
        @input="$ev => update($ev)"
        data-field="sorting"
        tag="kvass-sorting"
        :config="sortingConfig"
        :mode="buildMode ? 'build' : 'sort'"
        source="https://cdn.jsdelivr.net/npm/@kvass/sorting@latest/dist/sorting.js"
        :labels="
          JSON.stringify({
            addAll: $t('addAll'),
            cancel: $t('cancel'),
            confirm: $t('confirm'),
            reset: $t('reset'),
            title: $t('sortingTitle'),
            subtitle: $t('sortingSubtitle'),
            alert: $t('sortingAlert'),
          })
        "
      />
    </ModalComponent>
  </div>
</template>

<script>
import Webcomponent from '@kvass/custom-fields/src/Fields/Webcomponent'
import { ModalComponent } from 'vue-elder-modal'
import fetch from '@/fetch'
import { getBuilderBlocks, addMissingBlocks, withKnownBlocks } from './utils'
import { DropdownComponent } from 'vue-elder-dropdown'
import getCoverThumbnail from '@/utils/getCoverThumbnail'

function getValue(path, data) {
  return path.split('.').reduce((acc, key) => {
    if (acc && key in acc) return acc[key]
  }, data)
}

function replaceNestedPlaceholders(str, data) {
  if (typeof str !== 'string') return str

  return str.replace(/{([\w.-]+)}/g, (_, path) => {
    if (!path) return str
    const value = getValue(path, data)
    return value !== undefined ? value : `{${path}}`
  })
}

function getThumbnail(str, data) {
  if (typeof str !== 'string') return str

  const [, path] = str.match(/^{([\w.-]+)}$/) || []
  if (!path) return str

  return getCoverThumbnail(getValue(path, data) || []) || 'fa-pro-regular:image'
}

export default {
  props: {
    item: Object,
    value: Array,
    customFieldSchemas: { type: Array, default: () => [] },
    placement: [Array, String],
    build: Boolean,
    //add the default blocks that are new since the layout was saved and store the listed blocks on save -> see addMissingBlocks
    addMissing: Boolean,
    ignoreTheme: Boolean,
    selectFromPreset: Boolean,
    presetTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      open: false,
      preset: null,
      presets: [],
    }
  },
  watch: {
    preset(newValue, oldValue) {
      if (newValue) {
        const value = this.presets.find(i => i.id === newValue)

        if (value) {
          this.$emit('input', value.layout)
        }
      }
    },
  },

  computed: {
    valueComp() {
      const value = this.addMissing ? addMissingBlocks(this.value, this.customFieldSchemas, this.placement) : this.value
      //blocks that are no longer listed (e.g. residential-shop without a shop) -> hidden instead of an empty item
      const keys = this.addMissing && getBuilderBlocks(this.customFieldSchemas, this.placement).map(block => block.key)
      return (value || []).filter(cf => {
        if (keys && !keys.includes(this.$path('data.reference', cf) || cf.key)) return false
        return this.$path('block.display.builder', cf) !== false
      })
    },
    triggerLabel() {
      if (this.enablePresetSelect || this.buildMode) return 'Rediger felter'

      return this.$t('sortingTitle')
    },
    buildMode() {
      return this.build
    },
    isNuxt() {
      if (!this.item) return
      if ((this.item.config || '').includes('custom-subpage')) return true
      //no theme to check (e.g. residential template) -> the page decides when the builder is shown
      if (this.ignoreTheme) return true
      const { NuxtPages, NuxtProjects } = this.$store.state.enums
      return [...NuxtPages, ...NuxtProjects].some(i =>
        [this.$path('theme.template', this.item), this.item.theme].includes(i),
      )
    },
    enablePresetSelect() {
      return this.presets.length && this.selectPreset
    },
    sortingConfig() {
      if (!this.item) return ''

      let mappedItems = getBuilderBlocks(this.customFieldSchemas, this.placement).map(item => {
        return Object.fromEntries(
          Object.entries(item).map(([key, value]) => {
            if (key === 'thumbnail') return [key, getThumbnail(value, this.item)]
            return [key, replaceNestedPlaceholders(value, this.item)]
          }),
        )
      })

      if (!mappedItems.length) return
      return JSON.stringify(mappedItems)
    },
  },
  methods: {
    update(value) {
      switch (value.state) {
        case 'save': {
          let layout = value.value

          //sort mode returns an empty list when the order is the default -> store the default order.
          //an empty layout with the known blocks would mean that all blocks are removed
          if (this.addMissing && !this.buildMode && !(layout || []).length) {
            layout = getBuilderBlocks(this.customFieldSchemas, this.placement).map(block => ({
              key: block.key,
              data: {},
            }))
          }

          //store the listed blocks -> blocks added in code later are added to the layout, removed blocks stay removed
          this.$emit(
            'input',
            this.addMissing ? withKnownBlocks(layout, this.customFieldSchemas, this.placement) : layout,
          )
          this.open = false
          break
        }
        case 'reset': {
          this.$emit('input', value.value)
          break
        }
        default: {
          this.open = false
          break
        }
      }
    },
    selectPreset() {
      return fetch(
        `/api/resource/post?field.id=slug&field.thumbnail=media.cover&field.title=title&field.layout=layout&field.source=customFieldsConfig.0.source&references=${this.item.reference.ref}&disableFilterByIds=true`,
      )
        .then(res => res.json())
        .then(data => {
          return (this.presets = data.filter(i => {
            return Boolean(i.layout && i.layout.length) && this.item.config === i.source && this.item.slug !== i.id
          }))
        })
    },
  },
  mounted() {
    if (this.selectFromPreset) {
      this.selectPreset()
    }
  },

  components: {
    Webcomponent,
    ModalComponent,
    DropdownComponent,
  },
}
</script>

<style lang="scss">
.field-builder {
  &__content {
    max-width: fit-content;
  }
  &__category {
    opacity: 0.8;
    font-weight: bold;
    border-top: 1px solid var(--border-color);

    padding: 0.75rem 1rem 0.5rem;

    display: block;
    & > * {
      padding: 0;
    }
  }
}
</style>
